import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useCallback, useState } from 'react';
import { useTenantContext } from 'src/context/useTenantContext';
import { tenantIssues } from 'src/services/apis/tenantRequests';
import { QUERIES } from 'src/utils/react-query';

export const useEmployeeMonthlyIssues = (employeeId: string | null) => {
  const { orgSlug, restaurantId, branchId } = useTenantContext();
  const [page] = useState(1);
  const [filters, setFilters] = useState<{
    limit: number;
    startDate: Date | null | string;
    endDate: Date | null | string;
    employeeId: string | null;
  }>({
    limit: 100,
    startDate: dayjs().startOf('month').toDate().toISOString(),
    endDate: dayjs().endOf('month').endOf('day').toDate().toISOString(),
    employeeId: null,
  });
  const getDefaultFilters = useCallback(() => {
    return {
      limit: 100,
      startDate: dayjs().startOf('month').toDate().toISOString(),
      endDate: dayjs().endOf('month').endOf('day').toDate().toISOString(),
      employeeId: employeeId,
    };
  }, [employeeId]);
  // Reset the filters when the scope changes, derived during render rather than in an
  // effect: React re-renders with the new value before committing, where the effect
  // committed the stale filters first and then immediately replaced them.
  const filterScope = String(employeeId);
  const [previousFilterScope, setPreviousFilterScope] = useState(filterScope);
  if (previousFilterScope !== filterScope) {
    setPreviousFilterScope(filterScope);
    setFilters(getDefaultFilters());
  }
  const query = useQuery(
    [
      QUERIES.EMPLOYEE_MONTHLY_ISSUES,
      orgSlug,
      restaurantId,
      branchId,
      page,
      {
        employeeId: employeeId,
        startDate: filters.startDate,
        endDate: filters.endDate,
      },
    ],
    ({ queryKey: [_key, _orgSlug, _restaurantId, _branchId, _page, _filters] }: { queryKey: any }) => {
      const convertedFilters: string[] = [];
      convertedFilters.push(`employeeId||$eq||${_filters.employeeId}`);
      convertedFilters.push(`createdAt||$gte||${_filters.startDate}`);
      convertedFilters.push(`createdAt||$lte||${_filters.endDate}`);
      return tenantIssues(
        { orgSlug: _orgSlug as string, restaurantId: _restaurantId as string, branchId: _branchId as string },
        {
          page: _page as number,
          limit: _filters.limit,
          filter: convertedFilters,
        }
      );
    },
    {
      cacheTime: 0,
      refetchOnMount: true,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      enabled: !!employeeId,
    }
  );
  return {
    query,
  };
};
